// import { HttpClient } from '@angular/common/http';
// import { Injectable } from '@angular/core';
// import { Observable, of } from 'rxjs';
// import { catchError, switchMap, tap } from 'rxjs/operators';
// import { IResult } from '../../dsCore/interfaces';
// import { IFullAccountDetails } from '../../user-account/utils/IFullAccountDetails';


import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthRedirectService } from './auth-redirect.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { IResult } from '../../dsCore/interfaces';
import { IFullAccountDetails } from '../../user-account/utils/IFullAccountDetails';

// models/current-user.models.ts

export interface IClaim {
  type: string;
  value: string;
}

export interface CurrentUserBasic {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  roles: string[];
  logoutUrl: string;
  sessionExpiresIn: number | null;
  sessionState: string | null;
  isAuthenticated: boolean;
}

/** Final object that components will consume */
export interface CurrentUser extends CurrentUserBasic {
  /** Set when /fulldetails completes */
  full?: IFullAccountDetails | null;
  /** Convenience flags derived from effectivePermissions */
  hasCapex: boolean;
  hasOpex: boolean;
}


// services/current-user.service.ts
@Injectable({ providedIn: 'root' })
export class CurrentUserService {
  /** In-memory cache/broadcast of the current user state */
  private readonly userSubject = new BehaviorSubject<CurrentUser | null>(null);
  /** Public observable for components/guards (use async pipe where possible) */
  public readonly user$ = this.userSubject.asObservable();

  /** Prevent duplicate initialisation calls */
  private initialised = false;
  private fullAccountDetailsRequest$?: Observable<IResult<IFullAccountDetails>>;

  constructor(private http: HttpClient, private authRedirect: AuthRedirectService) { }

  // Broadcast an app-wide auth-change signal so legacy AngularJS services
  // can invalidate any cached user-access data during login/logout transitions.
  private notifyAuthChanged() {
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new Event('ds-auth-changed'));
    }
  }

  /**
   * Initialise the user once. Safe to call multiple times; only the first call triggers HTTP.
   * Returns user$ so call-sites can `await` first emission if they need to.
   */
  init(): Observable<CurrentUser | null> {
    if (this.initialised) {
      return this.user$;
    }
    this.initialised = true;

    this.http.get<IClaim[]>('/bff/user').pipe(
      map((claims) => this.mapClaimsToBasicUser(claims)),
      // Broadcast basic user immediately
      tap((basic) => this.userSubject.next(basic)),
      // If authenticated, fetch the enriched details
      switchMap((basic) => {
        if (!basic.isAuthenticated) {
          return of(basic as CurrentUser);
        }
        return this.getFullAccountDetails().pipe(
          map((result) => {
            const full = result?.data ?? null;
            const effectivePermissions = full?.effectivePermissions ?? {} as any;

            const hasCapex = !!(effectivePermissions?.CAPEX && effectivePermissions.CAPEX.accessType && effectivePermissions.CAPEX.accessType !== 'None');
            const hasOpex = !!(effectivePermissions?.OPEX && effectivePermissions.OPEX.accessType && effectivePermissions.OPEX.accessType !== 'None');

            const enriched: CurrentUser = { ...basic, full, hasCapex, hasOpex };
            return enriched;
          }),
          catchError((err) => {
            // Keep the basic authenticated user even if enrichment fails
            console.error('Failed to enrich current user:', err);
            return of({ ...basic, full: null, hasCapex: false, hasOpex: false } as CurrentUser);
          })
        );
      }),
      catchError((err) => {
        console.error('Failed to fetch current user claims:', err);
        const unauthenticated: CurrentUser = {
          id: '',
          name: '',
          firstName: '',
          lastName: '',
          email: '',
          roles: [],
          logoutUrl: '',
          sessionExpiresIn: null,
          sessionState: null,
          isAuthenticated: false,
          full: null,
          hasCapex: false,
          hasOpex: false,
        };
        return of(unauthenticated);
      })
    ).subscribe((finalUser) => {
      this.userSubject.next(finalUser);
    });

    return this.user$;
  }

  /** Convenience getter for synchronous access (e.g., in guards) */
  getSnapshot(): CurrentUser | null {
    return this.userSubject.value;
  }

  /** Emits the first authenticated user or completes with an error if unauthenticated. */
  requireAuthenticated$(): Observable<CurrentUser> {
    // Ensure init has been called by the app shell
    return this.user$.pipe(
      filter((u): u is CurrentUser => !!u),
      filter((u) => u.isAuthenticated === true),
      take(1)
    );
  }

  /** Explicit second endpoint – used internally by init(), still available if needed elsewhere */
  getFullAccountDetails(): Observable<IResult<IFullAccountDetails>> {
    if (!this.fullAccountDetailsRequest$) {
      this.fullAccountDetailsRequest$ = this.http.get<IResult<IFullAccountDetails>>(`/api/currentuser/fulldetails`).pipe(
        shareReplay(1)
      );
    }

    return this.fullAccountDetailsRequest$;
  }

  /** Log in via BFF */
  login(currentUrl: string) {
    this.notifyAuthChanged();
    if (this.authRedirect) {
      this.authRedirect.redirectToLogin(currentUrl);
    } else {
      window.location.href = `/bff/login?returnUrl=${encodeURIComponent(currentUrl)}`;
    }
  }

  /** Log out via BFF (uses claim-sourced logoutUrl when available) */
  logout() {
    this.notifyAuthChanged();
    const url = this.userSubject.value?.logoutUrl || '/bff/logout';
    if (this.authRedirect) {
      this.authRedirect.redirectToUrl(url);
    } else {
      window.location.href = url;
    }
  }

  // -------- Helpers --------

  private mapClaimsToBasicUser(claims: IClaim[]): CurrentUser {
    const get = (type: string) => claims.find((c) => c.type === type)?.value ?? '';
    const getAll = (type: string) => claims.filter((c) => c.type === type).map((c) => c.value);

    const sessionExpiresRaw = claims.find((c) => c.type === 'bff:session_expires_in')?.value ?? null;
    const sessionExpiresIn = sessionExpiresRaw != null ? Number(sessionExpiresRaw) : null;

    const sessionState = claims.find((c) => c.type === 'bff:session_state')?.value ?? null;
    const logoutUrl = get('bff:logout_url') || '/bff/logout';

    const roles = getAll('role');

    // If we have a 'sub' and a 'name', take that as authenticated; adjust if your BFF differs.
    const isAuthenticated = !!get('sub');

    return {
      id: get('sub'),
      name: get('name'),
      firstName: get('given_name'),
      lastName: get('family_name'),
      email: get('email'),
      logoutUrl,
      sessionExpiresIn,
      sessionState,
      roles,
      isAuthenticated,
      // defaults until enrichment runs
      full: null,
      hasCapex: false,
      hasOpex: false,
    };
  }
}